// auditpm — Auditor dashboard ("My Audits"). Action-oriented: Action Required (severity-sorted),
// Submitted/Pending Outcome, Upcoming/Scheduled. Closed audits (office recorded the new bizSAFE expiry)
// are excluded entirely. Read-only/derived — does not change access, COI, closure, checklist, or evidence
// logic. Degrades gracefully if the Group-B audit tables aren't applied yet (COI + submission still work).
import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, isBizSAFEType } from '../lib/supabase'
import { format, parseISO, differenceInCalendarDays } from 'date-fns'
import { useAuth } from '../lib/AuthContext'
import { resolveExpiry, expiryTier } from '../lib/expiry'
import { resolveMyAuditor, slotForAuditor } from '../lib/auditorAssignment'
import { isIA, isOTE, oteFinished, oteJobStatus } from '../lib/service'
import { MY_AUDITS_JOB_COLUMNS } from '../lib/jobLists'
import { toast } from '../lib/toast'

const SCOPE_MIN = 10, REASON_MIN = 15
function meaningful(t, min) {
  const s = (t || '').trim()
  if (s.length < min) return false
  if (/^(.)\1*$/.test(s)) return false
  if (s.replace(/[^a-zA-Z]/g, '').length < 5) return false
  if (['na', 'n/a', 'nil', 'none', 'no', 'tbd', 'tba', 'no comment', 'not applicable', 'nothing', 'n.a.'].includes(s.toLowerCase())) return false
  return true
}
const applies = (item, appType) => {
  const r = item.applicability_rule
  if (!r || !r.audit_type) return true
  return r.audit_type.includes(appType)
}
const TONE = { red: '#E24B4A', green: '#639922', amber: '#E6A817', grey: 'var(--border2)' }

export default function MyAudits() {
  const { profile, user } = useAuth()
  const navigate = useNavigate()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const searchRef = useRef(null)

  useEffect(() => { if (profile?.name) load() }, [profile?.name])

  // Page-scoped Ctrl/Cmd+F → focus the My Audits search (only while this page is mounted). Skip if the
  // user is already typing in a field, or already in the search box (then do nothing special).
  useEffect(() => {
    function onKey(e) {
      if (!((e.ctrlKey || e.metaKey) && (e.key === 'f' || e.key === 'F'))) return
      const el = document.activeElement
      if (el === searchRef.current) return
      const tag = el && el.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el && el.isContentEditable)) return
      e.preventDefault()
      searchRef.current && searchRef.current.focus()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  async function load() {
    setLoading(true)
    const [{ data: jobs }, { data: cois }, { data: aus }] = await Promise.all([
      supabase.from('jobs').select(`${MY_AUDITS_JOB_COLUMNS}, clients(name, bizsafe_expiry_date)`),
      supabase.from('auditor_coi_responses').select('job_id, auditor_slot, auditor_name'),
      supabase.from('auditors').select('id, name, user_id, email'),   // email: same "which auditor am I" rule as the worksheet
    ])
    const al = aus || []
    const myAuditor = resolveMyAuditor(al, profile, user?.email)
    const slotOf = j => slotForAuditor(j, myAuditor, profile)
    // Mine, not cancelled, NOT closed (closure rule), and I'm assigned.
    // Internal Audit jobs drop off once finished (report sent, no close-out pending).
    const iaFinished = j => isIA(j.audit_type) && (j.ia_stage === 'done' || (!j.ia_stage && j.status === 'Completed'))
    const mine = (jobs || []).filter(j => !['Cancelled'].includes(j.status) && !j.auditor_closed && !iaFinished(j) && !(isOTE(j.audit_type) && oteFinished(j)) && slotOf(j))
    const ids = mine.map(j => j.id)
    const coiRows = cois || []

    // Group-B detail (optional). If the audit tables aren't applied yet, degrade to COI + submission.
    let gb = null
    if (ids.length) {
      try {
        const [reps, tmplItems, resp, ivs, fms, files, stat, reqs, outs] = await Promise.all([
          supabase.from('audit_reports').select('job_id, checklist_template_id, status').in('job_id', ids),
          supabase.from('checklist_template_items').select('id, template_id, support_type, applicability_rule').is('archived_at', null),
          supabase.from('audit_checklist_responses').select('job_id, template_item_id, result, remarks').in('job_id', ids),
          supabase.from('audit_interviews').select('job_id, clause_ref, response').in('job_id', ids),
          supabase.from('audit_report_frontmatter').select('job_id, audit_scope_additional').in('job_id', ids),
          supabase.from('audit_evidence_files').select('job_id, evidence_type').in('job_id', ids).is('archived_at', null),
          supabase.from('audit_evidence_status').select('job_id, evidence_key, missing_reason').in('job_id', ids),
          supabase.from('evidence_requirements').select('checklist_type, evidence_key, level').is('archived_at', null),
          supabase.from('audit_outstanding_items').select('job_id, status').in('job_id', ids).is('archived_at', null),
        ])
        if (reps.error) throw reps.error
        const itemsByTpl = {}; (tmplItems.data || []).forEach(it => { (itemsByTpl[it.template_id] = itemsByTpl[it.template_id] || []).push(it) })
        const respByJob = {}; (resp.data || []).forEach(r => { (respByJob[r.job_id] = respByJob[r.job_id] || {})[r.template_item_id] = r })
        const ivByJob = {}; (ivs.data || []).forEach(v => { (ivByJob[v.job_id] = ivByJob[v.job_id] || []).push(v) })
        const fmByJob = {}; (fms.data || []).forEach(f => { fmByJob[f.job_id] = f })
        const filesByJob = {}; (files.data || []).forEach(f => { (filesByJob[f.job_id] = filesByJob[f.job_id] || []).push(f) })
        const statByJob = {}; (stat.data || []).forEach(s => { (statByJob[s.job_id] = statByJob[s.job_id] || {})[s.evidence_key] = s })
        const reqsByType = {}; (reqs.data || []).forEach(r => { (reqsByType[r.checklist_type] = reqsByType[r.checklist_type] || []).push(r) })
        const outByJob = {}; (outs.data || []).forEach(o => { (outByJob[o.job_id] = outByJob[o.job_id] || []).push(o) })
        const repByJob = {}; (reps.data || []).forEach(r => { repByJob[r.job_id] = r })
        gb = { itemsByTpl, respByJob, ivByJob, fmByJob, filesByJob, statByJob, reqsByType, outByJob, repByJob }
      } catch (e) { gb = null }
    }

    const today = new Date()
    const list = mine.map(j => computeJob(j, slotOf(j), coiRows, gb, today))
    setRows(list)
    setLoading(false)
  }

  function openPlan(coiToken) {
    if (!coiToken) { toast('Audit plan not sent yet', 'info', { detail: 'The office will send it to you' }); return }
    window.open(`${window.location.origin}/coi/${coiToken}`, '_blank')
  }

  if (loading) return <div style={{ color: 'var(--text3)' }}>Loading…</div>

  const q = query.trim().toLowerCase()
  const filtered = q
    ? rows.filter(r => (r.client || '').toLowerCase().includes(q) || (r.auditType || '').toLowerCase().includes(q) || (r.scope || '').toLowerCase().includes(q))
    : rows
  const A = filtered.filter(r => r.section === 'A').sort((a, b) => a.severity - b.severity || dateAsc(a, b))
  const B = filtered.filter(r => r.section === 'B').sort((a, b) => (b.submittedAt || '').localeCompare(a.submittedAt || ''))
  const C = filtered.filter(r => r.section === 'C').sort(dateAsc)

  return (
    <div>
      <div className="page-header"><h1>My Audits</h1></div>
      <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 10 }}>
        {A.length} need action · {B.length} pending outcome · {C.length} upcoming
      </div>

      {rows.length > 0 && (
        <input
          ref={searchRef}
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search client or audit type…"
          style={{ width: '100%', maxWidth: 360, marginBottom: 16, padding: '7px 10px', fontSize: 13 }}
        />
      )}

      {rows.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--text3)' }}>You have no assigned audits.</div>
      )}
      {rows.length > 0 && filtered.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '24px 20px', color: 'var(--text3)' }}>No audits match “{query}”.</div>
      )}

      {A.length > 0 && <SectionTable title="Action Required" accent={TONE.red} rows={A} navigate={navigate} openPlan={openPlan} />}
      {B.length > 0 && <SectionTable title="Submitted / Pending Outcome" accent={TONE.grey} rows={B} navigate={navigate} openPlan={openPlan} />}
      {C.length > 0 && <SectionTable title="Upcoming / Scheduled" accent={TONE.amber} rows={C} navigate={navigate} openPlan={openPlan} />}
    </div>
  )
}

// Table-style section (Tracker-consistent): one header row, then rows grouped by audit month
// (chronological; undated → "Date TBD" last). Compact operational rows — no cards.
const GRID = '1.1fr 0.55fr 0.82fr 0.88fr 0.5fr 1.5fr minmax(180px, 1.5fr)'

function monthName(k) {
  const [y, m] = k.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
}

function HeaderRow() {
  const c = { fontSize: 13, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.3 }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 8, padding: '6px 12px', borderBottom: '1px solid var(--border)' }}>
      <div style={c}>Client</div><div style={c}>Type</div><div style={c}>Audit date</div><div style={c}>Expiry</div><div style={c}>Urgency</div><div style={c}>Next action</div><div style={c}>Actions</div>
    </div>
  )
}

function Row({ r, navigate, openPlan }) {
  const t = r.isRenewal && r.expiry ? expiryTier(r.expiry) : null
  const expHot = t && (t.tier === 'overdue' || t.tier === 'urgent')
  const nextColor = r.tone === 'red' ? '#791F1F' : r.tone === 'green' ? '#27500a' : 'var(--text2)'
  return (
    <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 8, padding: '7px 12px', borderBottom: '1px solid var(--border)', alignItems: 'center', fontSize: 13 }}>
      <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.client}>{r.client}</div>
      <div>
        {r.isIA
          ? <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.3, padding: '1px 7px', borderRadius: 10, background: '#e0f2f2', color: '#006363', whiteSpace: 'nowrap' }}>INTERNAL AUDIT</span>
          : <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.3, padding: '1px 7px', borderRadius: 10, background: r.isRenewal ? '#EAF0FB' : 'var(--bg2)', color: r.isRenewal ? '#1F4F91' : 'var(--text3)', whiteSpace: 'nowrap' }}>{r.isRenewal ? 'RENEWAL' : 'NEW'}</span>}
      </div>
      <div style={{ whiteSpace: 'nowrap', color: r.overdueBadge ? '#791F1F' : (r.auditDate ? 'var(--text)' : 'var(--text3)') }}>
        {r.auditDate ? format(parseISO(r.auditDate), 'd MMM yyyy') : 'TBD'}
        {r.overdueBadge && <span style={{ marginLeft: 5, fontSize: 13 }}>{r.overdueBadge}</span>}
      </div>
      <div style={{ whiteSpace: 'nowrap', color: expHot ? 'var(--danger)' : 'var(--text)' }}>
        {r.isRenewal && r.expiry ? format(parseISO(r.expiry), 'd MMM yyyy') : ''}
      </div>
      <div>{t && t.tier !== 'ok' && <span className={`badge ${t.badge}`} style={{ fontSize: 13 }}>{t.label}</span>}</div>
      <div style={{ color: nextColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.nextStep}>{r.nextStep}</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {r.actions.map(a => (
          <button key={a.label} className={`sm${a.primary ? ' primary' : ''}`} style={{ fontSize: 13 }}
            onClick={() => a.kind === 'plan' ? openPlan(r.coiToken)
              : a.kind === 'ia' ? navigate(`/jobs/${r.id}/ia`)
              : a.kind === 'ote' ? navigate(`/jobs/${r.id}/ote`)
              : a.kind === 'iaplan' ? window.open(`${window.location.origin}/ia-plan/${r.planToken}`, '_blank')
              : navigate(`/jobs/${r.id}/execute`)}>{a.label}</button>
        ))}
      </div>
    </div>
  )
}

function SectionTable({ title, accent, rows, navigate, openPlan }) {
  const groups = {}
  for (const r of rows) { const k = r.auditDate ? r.auditDate.slice(0, 7) : 'TBD'; (groups[k] = groups[k] || []).push(r) }
  const keys = Object.keys(groups).sort((a, b) => a === 'TBD' ? 1 : b === 'TBD' ? -1 : a.localeCompare(b))
  return (
    <div style={{ marginBottom: 20 }}>
      <h2 style={{ marginBottom: 10, fontSize: 14, borderLeft: `3px solid ${accent}`, paddingLeft: 8 }}>{title}</h2>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <HeaderRow />
        {keys.map(k => (
          <div key={k}>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text3)', padding: '5px 12px', background: 'var(--bg2)', borderBottom: '1px solid var(--border)' }}>{k === 'TBD' ? 'Date TBD' : monthName(k)}</div>
            {groups[k].map(r => <Row key={r.id} r={r} navigate={navigate} openPlan={openPlan} />)}
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------- OTE endorsement job (no COI / audit plan / bizSAFE expiry; one auditor) ----------
function computeOTEJob(j, slot, today) {
  const auditDate = j.audit_date || null
  const d = auditDate ? differenceInCalendarDays(parseISO(auditDate), today) : null
  const overdue = d != null && d < 0, isToday = d === 0, soon = d != null && d >= 0 && d <= 7
  const open = primary => ({ label: 'Open job', kind: 'ote', primary })
  const stage = j.ote_stage || 'scheduled'
  const working = ['scheduled', 'confirmed', 'in_progress'].includes(stage)
  let section = 'C', severity = 9, tone = 'grey', nextStep = oteJobStatus(j), actions = [open(false)]
  if (stage === 'awaiting_closeout') { section = 'B'; nextStep = 'Waiting for the client to send what is still needed' }
  else if (['signing', 'awaiting_payment', 'sending', 'not_endorsed'].includes(stage)) section = 'B'
  else if (stage === 'ready_to_send') { section = 'A'; severity = 2; tone = 'amber'; nextStep = 'Signed — send the endorsement to the client'; actions = [open(true)] }
  else if (overdue || isToday || stage === 'in_progress') {
    section = 'A'; severity = isToday ? 5 : 2; tone = overdue ? 'red' : 'amber'
    nextStep = isToday ? 'Job today' : 'Complete the verification and sign'; actions = [open(true)]
  } else if (soon) { section = 'A'; severity = 6; tone = 'amber'; nextStep = 'Job soon'; actions = [open(true)] }
  return {
    id: j.id, client: j.clients?.name || '—', auditType: j.audit_type, scope: '', auditDate, slot, coiToken: null,
    isIA: true, planToken: null, isRenewal: false, expiry: null,
    submitted: !working && stage !== 'awaiting_closeout', submittedAt: null, chips: {}, section, severity, tone, nextStep, actions,
    overdueBadge: overdue && working ? `${Math.abs(d)}d ago` : isToday ? 'today' : null, _d: d,
  }
}

// ---------- Client Internal Audit job (no COI / acknowledgement / bizSAFE expiry) ----------
function computeIAJob(j, slot, today) {
  const auditDate = j.audit_date || null
  const d = auditDate ? differenceInCalendarDays(parseISO(auditDate), today) : null
  const overdue = d != null && d < 0, isToday = d === 0, soon = d != null && d >= 0 && d <= 7
  const open = primary => ({ label: 'Open audit', kind: 'ia', primary })
  let section = 'C', severity = 9, tone = 'grey', nextStep = 'Scheduled', actions = [open(false)]
  if (j.ia_stage === 'returned') {
    section = 'A'; severity = 1; tone = 'red'; nextStep = 'Returned for revision — update the report and send again'; actions = [open(true)]
  } else if (j.ia_stage === 'awaiting_payment' || j.ia_stage === 'sending') {
    section = 'B'; nextStep = j.ia_stage === 'sending' ? 'Sending the report to the client' : 'Submitted — report will be sent once payment is received'
  } else if (j.ia_stage === 'awaiting_closeout') {
    section = 'B'; nextStep = 'Report sent — waiting for the client’s close-out evidence'
  } else if (!j.ia_plan_token) {
    nextStep = 'Audit plan not issued yet'
  } else if (overdue || isToday) {
    section = 'A'; severity = isToday ? 5 : 2; tone = overdue ? 'red' : 'amber'
    nextStep = overdue ? 'Audit done — complete the worksheet and send the report' : 'Audit today'; actions = [open(true)]
  } else if (soon) {
    section = 'A'; severity = 6; tone = 'amber'; nextStep = 'Audit soon'; actions = [open(true)]
  }
  // Two auditors: who is waiting for whom (before the report is submitted).
  if (j.auditor2_name && (!j.ia_stage || j.ia_stage === 'returned') && j.ia_plan_token) {
    const signed2 = (j.report_signatures || {}).auditor2?.name === j.auditor2_name
    if (slot === 'auditor2' && !signed2 && (overdue || isToday)) { nextStep = 'Waiting for your signature — check the report, then sign it (Step 3)'; actions = [open(true)] }
    if (slot === 'auditor1' && signed2) { section = 'A'; severity = 1; tone = 'amber'; nextStep = `${j.auditor2_name} has signed — check and send the report`; actions = [open(true)] }
    if (slot === 'auditor2' && signed2) { section = 'B'; nextStep = `You have signed — ${j.auditor_name} (lead) will send the report` }
  }
  if (j.ia_plan_token) actions.push({ label: 'View Plan', kind: 'iaplan' })
  return {
    id: j.id, client: j.clients?.name || '—', auditType: j.audit_type, scope: '', auditDate, slot, coiToken: null,
    isIA: true, planToken: j.ia_plan_token, isRenewal: false, expiry: null,
    submitted: !!j.ia_stage, submittedAt: j.ia_report_sent_at || null, chips: {}, section, severity, tone, nextStep, actions,
    overdueBadge: overdue && !j.ia_stage ? `${Math.abs(d)}d ago` : isToday ? 'today' : null, _d: d,
  }
}

// ---------- per-job computation ----------
function computeJob(j, slot, coiRows, gb, today) {
  if (isOTE(j.audit_type)) return computeOTEJob(j, slot, today)
  if (isIA(j.audit_type)) return computeIAJob(j, slot, today)
  // appType drives CHECKLIST applicability only (renewal-only items). What is SHOWN to the user
  // (New/Renewal pill + expiry) comes from the single source of truth: the bizSAFE expiry.
  const appType = j.form_data?.application_type || 'New Application'
  const expiry = resolveExpiry(j.clients, j.form_data)
  const isRenewal = !!expiry
  const isBizsafe = isBizSAFEType(j.audit_type)   // registry-aware (handles legacy/variant labels)
  const checklistType = isBizsafe ? 'bizsafe_rm_l3' : null
  const slotName = slot === 'auditor2' ? j.auditor2_name : j.auditor_name
  const coiToken = (j.auditor_coi_tokens || {})[slot] || null
  const coiDone = coiRows.some(c => c.job_id === j.id && c.auditor_slot === slot && c.auditor_name === slotName)
  const submitted = !!j.report_submitted
  const submittedAt = j.report_submitted_at || null
  const auditDate = j.audit_date || null
  const d = auditDate ? differenceInCalendarDays(parseISO(auditDate), today) : null
  const overdue = d != null && d < 0
  const isToday = d === 0
  const soon = d != null && d >= 0 && d <= 7

  // Group-B readiness (if available + bound)
  let checklistComplete = false, checklistStarted = false, interviewsOk = true, scopeOk = false, evOk = true
  let blockerText = null, hasDetail = false
  const rep = gb?.repByJob?.[j.id]
  if (gb && rep?.checklist_template_id) {
    hasDetail = true
    const items = (gb.itemsByTpl[rep.checklist_template_id] || []).filter(it => applies(it, appType))
    const resp = gb.respByJob[j.id] || {}
    const answered = items.filter(it => resp[it.id]?.result)
    checklistStarted = answered.length > 0
    checklistComplete = items.length > 0 && answered.length === items.length
    const ipAnswered = items.filter(it => /IP/.test(it.support_type || '') && resp[it.id]?.result === 'Yes')
    interviewsOk = ipAnswered.every(it => (gb.ivByJob[j.id] || []).some(v => v.clause_ref === it.clause_ref && (v.response || '').trim()))
    const remarkMissing = items.some(it => { const r = resp[it.id]; return r && (r.result === 'No' || r.result === 'N/A') && !meaningful(r.remarks, REASON_MIN) })
    scopeOk = meaningful(gb.fmByJob[j.id]?.audit_scope_additional, SCOPE_MIN)
    const reqs = (gb.reqsByType[checklistType] || []).filter(r => r.level !== 'optional')
    evOk = reqs.every(req => (gb.filesByJob[j.id] || []).some(f => f.evidence_type === req.evidence_key)
      || (req.level === 'expected' && meaningful(gb.statByJob[j.id]?.[req.evidence_key]?.missing_reason, REASON_MIN)))
    const unanswered = items.length - answered.length
    blockerText = !scopeOk ? 'Scope note (actual processes/activities audited) required'
      : remarkMissing ? 'A remark is required for a No / N-A result'
      : !interviewsOk ? 'An interview response is required for an IP item'
      : !evOk ? 'Required / expected evidence is missing (or its reason)'
      : unanswered > 0 ? `${unanswered} checklist item${unanswered > 1 ? 's' : ''} still unanswered`
      : null
  }
  const blocking = hasDetail && (!scopeOk || !interviewsOk || !evOk || (blockerText && blockerText.includes('remark')))
  const ready = hasDetail && coiDone && checklistComplete && interviewsOk && scopeOk && evOk
  // Returned = the office or WSHC sent it back. New flow uses job.workflow_state; keep the legacy
  // audit_reports.status check for older records.
  const returned = rep?.status === 'Returned for Revision' || j.workflow_state === 'Returned for Revision'

  // chips
  const chips = {
    coi: !coiToken ? 'na' : (coiDone ? 'ok' : 'warn'),
    checklist: !hasDetail ? 'na' : (checklistComplete ? 'ok' : checklistStarted ? 'warn' : 'na'),
    interviews: !hasDetail ? 'na' : (interviewsOk ? (checklistStarted ? 'ok' : 'na') : 'warn'),
    evidence: !hasDetail ? 'na' : (evOk ? 'ok' : 'warn'),
    submitted: submitted ? 'ok' : 'na',
  }
  const overdueBadge = overdue ? `${Math.abs(d)}d overdue` : isToday ? 'today' : null

  // section + severity + tone + next step + actions
  let section = 'C', severity = 9, tone = 'grey', nextStep = 'Scheduled', actions = [{ label: 'View Plan', kind: 'plan' }]

  if (submitted && !returned) {
    section = 'B'; tone = 'grey'
    const ws = j.workflow_state
    const stageText = ws === 'Pending WSHC Outcome' ? 'awaiting WSHC outcome'
      : ws === 'Pending Office Review' ? 'pending office review'
      : 'pending office / WSHC outcome'
    nextStep = `Submitted${submittedAt ? ` ${format(parseISO(submittedAt), 'd MMM')}` : ''} — ${stageText}`
    actions = [{ label: 'View Submission', kind: 'exec' }]
  } else if (returned) {
    section = 'A'; severity = 1; tone = 'red'
    nextStep = 'Returned for revision — address comments and resubmit'
    actions = [{ label: 'Prepare Report', kind: 'exec', primary: true }]
  } else if (ready) {
    section = 'A'; severity = 4; tone = 'green'
    nextStep = 'Ready to submit'
    actions = [{ label: 'Submit Report', kind: 'exec', primary: true }, { label: 'View Plan', kind: 'plan' }]
  } else if (!coiDone) {
    if (overdue || soon) {
      section = 'A'; severity = 2; tone = 'red'
      nextStep = `COI pending — complete before the audit (${overdue ? 'overdue' : 'soon'})`
      actions = [{ label: 'View Plan & COI', kind: 'plan', primary: true }]
    } else {
      section = 'C'; tone = 'amber'
      nextStep = 'COI pending — can complete closer to the audit'
      actions = [{ label: 'View Plan & COI', kind: 'plan' }]
    }
  } else { // coiDone, not ready
    if (blocking) {
      section = 'A'; severity = (overdue || isToday) ? 2 : 3; tone = 'red'
      nextStep = blockerText || 'Missing required input'
      actions = [{ label: 'Prepare Report', kind: 'exec', primary: true }]
    } else if (checklistStarted) {
      section = 'A'; severity = (overdue || isToday) ? 2 : 5; tone = (overdue || isToday) ? 'red' : 'amber'
      nextStep = (overdue || isToday) ? 'Overdue — finish and submit the report' : (blockerText || 'Report in progress — continue')
      actions = [{ label: 'Prepare Report', kind: 'exec', primary: true }]
    } else if (overdue || isToday || soon) {
      section = 'A'; severity = 5; tone = 'amber'
      nextStep = overdue ? 'Audit overdue — start the report' : isToday ? 'Audit today — start the report' : 'Audit soon — start the report'
      actions = [{ label: 'Prepare Report', kind: 'exec', primary: true }, { label: 'View Plan', kind: 'plan' }]
    } else {
      section = 'C'; tone = 'grey'
      nextStep = 'Scheduled — COI done; start when ready'
      actions = [{ label: 'View Plan', kind: 'plan' }]
    }
  }

  // Two auditors (same lines as Internal Audit): who is waiting for whom, before the report is submitted. The second
  // auditor signs first (the report then locks); the lead checks and submits. Single-auditor jobs: unchanged.
  if (j.auditor2_id && !submitted) {
    const sig2 = (j.report_signatures || {}).auditor2
    const signed2 = !!sig2 && typeof sig2 === 'object' && (!j.auditor2_name || sig2.name === j.auditor2_name)
    if (slot === 'auditor2' && !signed2 && ready && !returned) { nextStep = 'Waiting for your signature — check the report, then sign it (Step 4)' }
    if (slot === 'auditor1' && !signed2 && ready && !returned) { nextStep = `Waiting for ${j.auditor2_name || 'the second auditor'} to sign the report before you can submit` }
    if (slot === 'auditor1' && signed2) { section = 'A'; severity = 1; tone = 'amber'; nextStep = `${j.auditor2_name} has signed — check and submit the report`; actions = [{ label: 'Submit Report', kind: 'exec', primary: true }] }
    if (slot === 'auditor2' && signed2) { section = 'B'; tone = 'grey'; nextStep = `You have signed — ${j.auditor_name} (lead) will submit the report`; actions = [{ label: 'Prepare Report', kind: 'exec' }] }
  }

  // View Plan must always be available, regardless of stage/section.
  if (!actions.some(a => a.kind === 'plan')) actions.push({ label: 'View Plan', kind: 'plan' })

  return {
    id: j.id, client: j.clients?.name || '—', auditType: j.audit_type, scope: j.scope || '', auditDate, slot, coiToken,
    isRenewal, expiry,
    submitted, submittedAt, chips, section, severity, tone, nextStep, actions, overdueBadge, _d: d,
  }
}

function dateAsc(a, b) {
  const ad = a.auditDate || '9999', bd = b.auditDate || '9999'
  return ad.localeCompare(bd)
}
